import {
  type ReactNode,
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
  Children,
} from 'react';
import {
  StackLayout,
  StackItem,
  ProgressBar,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';

type StepNavigationHelpers = {
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (index: number) => void;
};

type StepperContextValue = StepNavigationHelpers & {
  progress: number;
  currentStepIndex: number;
  totalSteps: number;
  isComplete: boolean;
};

const StepperContext = createContext<StepperContextValue | null>(null);

export const useStepNavigation = (): StepNavigationHelpers => {
  const ctx = useContext(StepperContext);
  if (!ctx) {
    throw new Error('useStepNavigation must be used within a <Stepper>');
  }
  return {
    goToNextStep: ctx.goToNextStep,
    goToPreviousStep: ctx.goToPreviousStep,
    goToStep: ctx.goToStep,
  };
};

export const useStepperProgress = () => {
  const ctx = useContext(StepperContext);
  if (!ctx) {
    throw new Error('useStepperProgress must be used within a <Stepper>');
  }
  return {
    progress: ctx.progress,
    currentStepIndex: ctx.currentStepIndex,
    totalSteps: ctx.totalSteps,
    isComplete: ctx.isComplete,
  };
};

const CONTAINER_WIDTH = '708px';

type StepperProps = {
  children?: ReactNode;
  showProgressBar?: boolean;
};

const StepperContentWrapper = ({ children }: { children: ReactNode }) => {
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  return (
    <StackLayout
      verticalAlignment={isMobile ? 'top' : 'center'}
      css={{ marginBlockStart: isMobile ? '$three' : '$none' }}
    >
      <StackItem
        css={{
          background: '$surfaceCardSecondary',
          padding: isMobile ? '$oneAndHalf' : '$two',
          width: isMobile ? '100%' : CONTAINER_WIDTH,
        }}
      >
        {children}
      </StackItem>
    </StackLayout>
  );
};

export const Stepper = ({ children, showProgressBar = true }: StepperProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps = Children.toArray(children).filter(Boolean);
  const totalSteps = steps.length;

  const isComplete = totalSteps === 0 || currentStepIndex >= totalSteps;

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.min(totalSteps, prev + 1));
  }, [totalSteps]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback(
    (index: number) => {
      if (totalSteps === 0) return;
      setCurrentStepIndex(Math.max(0, Math.min(totalSteps - 1, index)));
    },
    [totalSteps],
  );

  const progress =
    totalSteps === 0
      ? 0
      : Math.min(100, ((currentStepIndex + 1) / totalSteps) * 100);

  const contextValue = useMemo<StepperContextValue>(
    () => ({
      goToNextStep,
      goToPreviousStep,
      goToStep,
      progress,
      currentStepIndex,
      totalSteps,
      isComplete,
    }),
    [
      goToNextStep,
      goToPreviousStep,
      goToStep,
      progress,
      currentStepIndex,
      totalSteps,
      isComplete,
    ],
  );

  const currentStep =
    currentStepIndex < steps.length ? steps[currentStepIndex] : null;

  return (
    <StepperContext.Provider value={contextValue}>
      {showProgressBar && (
        <ProgressBar
          fill="brand"
          fillProgressValue={progress}
          css={{
            '.GDS-progress-bar-background .GDS-progress-bar-track': {
              borderRadius: '$none',
            },
            maxHeight: 'max-content',
          }}
        />
      )}
      <StepperContentWrapper>{currentStep}</StepperContentWrapper>
    </StepperContext.Provider>
  );
};
